import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { AppLayout } from "../../../containers/layout/app";
import { CustomerListWrapper } from "./styled";
import { BaseButton } from "../../../components/button/styled";
import AddIcon from "@mui/icons-material/Add";
import Cookies from "universal-cookie";
import { AppContext } from "../../../context/appContext";
import { CustomersTable } from "../../../containers/tables/customerstable";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllCustomersService } from "../../../utils/api/customerservice/customerlist/retrieveAllCustomers";

export const CustomerList = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);
	const dropdownRef = useRef<HTMLUListElement | null>(null);

	const [isFetching, setIsFetching] = useState(false);
	const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
		null,
	);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Customer Service");
	}, [setActiveNavItem]);

	const { data: verifiedCustomers } = useQuery({
		queryKey: ["customers", TOKEN, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAllCustomersService(
				TOKEN,
				paginationIndex,
			);
			const customers = response.data;
			if (!customers || customers.length === 0) return [];
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}

			// the idea is that completely verified customers are those that have both kycStatus and status (have account number) as verified
			const verifiedCustomers = customers.filter(
				(customer: { kycStatus: string; status: string }) =>
					customer.kycStatus === "verified" && customer.status === "verified",
			);

			setIsFetching(false);
			return verifiedCustomers;
		},
		enabled: !!TOKEN,
	});

	const handleCreateCustomerButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/customerservice/addcustomerlist");
	};

	const handleEllipsisButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return setSelectedCustomerId(id);
	};

	const handleActionItemClick = (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		id: string | null,
		action: string,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!id) return;
		switch (action) {
			case "View Account Details":
				navigate(`/customerlist/customer/${id}/view`);
				break;
			case "Edit Account Details":
				navigate(`/customerlist/customer/${id}/edit`);
				break;
			default:
				break;
		}
		return;
	};

	const handleDropDownClickOutside = (event: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			return setSelectedCustomerId(null);
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	useEffect(() => {
		if (selectedCustomerId !== null) {
			document.addEventListener("mousedown", handleDropDownClickOutside);
		} else {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		};
	}, [selectedCustomerId]);

	return (
		<AppLayout pageId={"Customer-List"} pageTitle={"Customer List"}>
			<CustomerListWrapper>
				<Box>
					<BaseButton
						variant="contained"
						bgcolor="var(--primary-color)"
						padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
						startIcon={<AddIcon />}
						sx={{
							width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							float: { miniTablet: "right" },
						}}
						onClick={handleCreateCustomerButtonClick}
					>
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Create Customer
						</Typography>
					</BaseButton>
				</Box>
				<Box component={"div"} className="customer-list-table-box">
					<CustomersTable
						rows={verifiedCustomers as any[]}
						dropdownRef={dropdownRef}
						selectedCustomerId={selectedCustomerId}
						handleActionItemClick={handleActionItemClick}
						handleEllipsisButtonClick={handleEllipsisButtonClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
			</CustomerListWrapper>
		</AppLayout>
	);
};
